import { createSelector } from 'reselect';
import type { Trip } from 'src/types/trips/Trips';
import type { ReduxState } from 'src/types/reduxState';
import { getTrip } from 'src/common/utils/reduxStoreSelectors';
import moment from 'moment';
import { END_DATE_FIELD, PICKUP_TIME_FIELD, PICKUP_TIME_FIELD_PRISTINE, PREFERRED_DATE_FIELD, RETURN_TIME_FIELD, SUGGESTED_DATES_FIELD } from 'src/inbox/constants';
import { MOMENT_FORMAT } from 'src/common/constants';
import formatDuration from 'date-fns/formatDuration';
import type { Immutable } from 'src/types/ImmutableMap';
import type { Offer } from 'src/types/offer/Offer';
import { getUserRole } from 'src/inbox/helpers';
import type { DateFormValues } from 'src/inbox/components/cards/Dates/form';

const selectOffer = (state: ReduxState) => state.offer;
const selectTrip = (tripPk: string) => (state: ReduxState) => getTrip(tripPk)(state);

/** Exported for tests only */
export const durationAsWords = (duration: moment.Duration) => formatDuration({
  days: Math.floor(duration.asDays()),
  hours: duration.hours(),
  minutes: duration.minutes(),
});

/**
 * Standardizes reading of date time and duration values. Avoids common errors like:
 * - accidentally creating moment with undefined values
 * - using unreliable values like trip.get('return_time') sc-68019
 * - comparing trip_length in different formats (which will never equal)
 */
export const getDateTimeDuration = (trip: Immutable<Trip>, offer?: Immutable<Offer>) => {
  const tripStartDate = trip.get(PREFERRED_DATE_FIELD);
  const tripStartTime = trip.get(PICKUP_TIME_FIELD);
  /** Equivalent to `moment(trip.get('trip_starts_at'), MOMENT_FORMAT.VALUE_AND_TIME_24)` */
  const tripStartDateTimeMoment = moment(`${tripStartDate} ${tripStartTime}`, MOMENT_FORMAT.VALUE_AND_TIME_24);
  const tripEndDate = trip.get(END_DATE_FIELD);
  const tripEndTime = trip.get(RETURN_TIME_FIELD);
  const tripEndDateTimeMoment = moment(`${tripEndDate} ${tripEndTime}`, MOMENT_FORMAT.VALUE_AND_TIME_24);
  /** trip.get('trip_length') would work too, if normalizing toJSON() first */
  const tripDuration = moment.duration(tripEndDateTimeMoment.diff(tripStartDateTimeMoment));

  const offerStartDate = offer?.get(PREFERRED_DATE_FIELD);
  const offerStartTime = offer?.get(PICKUP_TIME_FIELD);
  const offerEndDate = offer?.get(END_DATE_FIELD);
  const offerEndTime = offer?.get(RETURN_TIME_FIELD);

  /** Equivalent to `getTripValue(offer, trip, 'preferred_date')` */
  const offerTripStartDate = offerStartDate ?? tripStartDate;
  const offerTripEndDate = offerEndDate ?? tripEndDate;
  const offerTripStartTime = offerStartTime ?? tripStartTime;
  const offerTripEndTime = offerEndTime ?? tripEndTime;
  const offerTripStartDateTimeMoment = moment(`${offerTripStartDate} ${offerTripStartTime}`, MOMENT_FORMAT.VALUE_AND_TIME_24);
  const offerTripEndDateTimeMoment = moment(`${offerTripEndDate} ${offerTripEndTime}`, MOMENT_FORMAT.VALUE_AND_TIME_24);
  const offerTripDuration = moment.duration(
    offerTripEndDateTimeMoment.diff(offerTripStartDateTimeMoment),
  );

  const isOfferTripLengthModified = tripDuration.toJSON() !== offerTripDuration.toJSON();
  const isOfferDatesModified = Boolean((offerStartDate || offerEndDate) && (
    (offerStartDate && offerStartDate !== tripStartDate)
    || (offerEndDate && offerEndDate !== tripEndDate)
  ));
  const isOfferTimesModified = Boolean((offerStartTime || offerEndTime) && (
    (offerStartTime
      && moment.duration(offerStartTime).asMinutes()
      !== moment.duration(tripStartTime).asMinutes())
    || (offerEndTime
      && moment.duration(offerEndTime).asMinutes()
      !== moment.duration(tripEndTime).asMinutes())
  ));

  /**
   * @deprecated backward compatible:
   * inquiries before Sep 2025 could be submitted with `trip.pickup_time: null`.
   */
  const hasPickupTime = Boolean(
    trip.get(PICKUP_TIME_FIELD_PRISTINE) || offer?.get(PICKUP_TIME_FIELD),
  );

  /**
   * @deprecated backward compatible: inquiries before Sep 2025 could include alternate dates.
   * Now, `trip.suggested_dates` is always size 1, where item 0 is equal to `trip.preferred_date`.
   */
  const suggestedDates: string[] = trip.get(SUGGESTED_DATES_FIELD).toJS() ?? [];
  const suggestedDatesWithCalculatedEnds: Pick<DateFormValues, 'preferred_date' | 'end_date'>[] = suggestedDates.map((suggestedDate) => ({
    preferred_date: suggestedDate,
    end_date: moment(`${suggestedDate} ${offerTripStartTime}`).add(offerTripDuration).format(MOMENT_FORMAT.VALUE),
  }));

  /**
   * Only owners should see alternate dates in the initial inquiry state "owner_direct_inquiry".
   * But after the owner replies with an offer, trip.suggsted_dates still contains multiple dates.
   */
  const suggestedDatesWithEnds = getUserRole(trip) === 'renter' ? suggestedDatesWithCalculatedEnds.slice(0, 1) : suggestedDatesWithCalculatedEnds;

  return {
    tripOnly: {
      startDateTimeMoment: tripStartDateTimeMoment,
      endDateTimeMoment: tripEndDateTimeMoment,
      sameDates: tripStartDate === tripEndDate,
      durationAsWords: durationAsWords(tripDuration),
      tripDuration,
    },
    offerTrip: {
      startTime: offerTripStartTime,
      endTime: offerTripEndTime,
      startDate: offerTripStartDate,
      endDate: offerTripEndDate,
      startDateTimeMoment: offerTripStartDateTimeMoment,
      endDateTimeMoment: offerTripEndDateTimeMoment,
      endDateTime: offerTripEndDateTimeMoment.format(MOMENT_FORMAT.VALUE_AND_TIME_24),
      sameDates: offerTripStartDate === offerTripEndDate,
      isOfferTripLengthModified,
      isOfferDatesModified,
      isOfferTimesModified,
      isOfferDateTimeDurationModified: (
        isOfferTripLengthModified || isOfferDatesModified || isOfferTimesModified
      ),
      hasPickupTime,
      durationMoment: moment.duration(offerTripDuration),
      durationISO: moment.duration(offerTripDuration).toJSON(),
      durationAsWords: durationAsWords(offerTripDuration),
      suggestedDatesWithEnds,
      /**
       * To spread at each fetch body callsite, after `...offer.toJS()`.
       * _(Offer is still used for non-dateTimeDuration values)_
       */
      dateTimeDurationPayload: {
        preferred_date: offerTripStartDate,
        pickup_time: offerTripStartTime,
        trip_length: moment.duration(offerTripDuration).toJSON(),
        /** Explicitly setting return_time prevents backend multi-day duration overrides */
        return_time: offerTripEndTime,
      },
    },
  };
};

/** Reads trip and offer values */
const dateTimeDurationSelector = (
  tripPk: Trip['pk'],
) => createSelector(
  [
    selectOffer,
    selectTrip(String(tripPk)),
  ],
  (offer, trip) => getDateTimeDuration(trip, offer),
);

export default dateTimeDurationSelector;
